import { RadioGroup } from "@headlessui/react";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { Form, useNavigation } from "@remix-run/react";
import type { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useEffect, useState } from "react";
import invariant from "tiny-invariant";
import { MIN_QUANTITY_AVAILABLE } from "~/constants";
import type { ProductVariant } from "~/shopify/types/gql/graphql";
import type { Option, ProductFlatten } from "~/types";
import { isBlackFridayPromoEnabled } from "~/utils/dates";
import loader from "../../images/loader.svg";
import Neum, { NeumButton } from "../neum.component";
import Badges from "./badges.component";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const createVariantsObject = (options: Option[], variants: ProductVariant[]): { [key: string]: ProductVariant[] } => {
  const initialVariants: { [key: string]: ProductVariant[] } = {};
  for (const option of options) {
    initialVariants[option.name] = variants;
  }
  return initialVariants;
};

const PurchaseGrid: FunctionComponent<{
  product: ProductFlatten;
  selectedOptions: { [key: string]: string };
  setSelectedOptions: Dispatch<SetStateAction<{ [key: string]: string }>>;
  optionsQueue: string[];
  setOptionsQueue: Dispatch<SetStateAction<string[]>>;
  isDesktop: boolean;
}> = ({ product, selectedOptions, setSelectedOptions, optionsQueue, setOptionsQueue, isDesktop }) => {
  invariant(product.options, "expected product.options to be present");
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [filteredVariants, setFilteredVariants] = useState(createVariantsObject(product.options, product.variants));
  const transition = useNavigation();
  const showOptions = product.variants.length > 1;

  const changePurchaseGridState = (newOptions: { [key: string]: string }, newQueue: string[]): void => {
    setSelectedOptions(newOptions);
    setOptionsQueue(newQueue);
  };

  // memoize option and dynamically create function to assign value
  const handleChangePerOption = (optionName: string) => {
    return (value: string) => {
      const newOptions = { ...selectedOptions, [optionName]: value };
      const newQueue = [...optionsQueue];
      if (!newQueue.includes(optionName)) {
        newQueue.push(optionName);
      }
      changePurchaseGridState(newOptions, newQueue);
    };
  };

  // check if at least one filteredVariant has the current optionValue
  // variant must be available for sale to be enabled
  const isOptionEnabled = (optionName: string, optionValue: string): boolean => {
    return filteredVariants[optionName].some((variant) => {
      if (variant.availableForSale && (variant.quantityAvailable ?? 0) >= MIN_QUANTITY_AVAILABLE) {
        return variant.selectedOptions.some((variantOption) => variantOption.value === optionValue);
      }
      return false;
    });
  };

  useEffect(() => {
    const varitantHasSelectedOptions = (
      variant: ProductVariant,
      selectedOptions: { [key: string]: string },
    ): boolean => {
      return Object.keys(selectedOptions).every((optionName) =>
        variant.selectedOptions.some(
          (option) => option.name === optionName && option.value === selectedOptions[optionName],
        ),
      );
    };

    // filter variants based on selected options queue
    const filterVariants = (selectedOptions: { [key: string]: string }, optionsQueue: string[]) => {
      invariant(product.options, "expected product.options to be present");
      //all variants available in all options by default
      const newFilteredVariants = createVariantsObject(product.options, product.variants);
      const queue = [...optionsQueue];
      // variants will be filtered each iteration based on selected options and previous variants
      while (queue.length > 0) {
        const currentOption = queue.shift() ?? "";
        //filter other option variants based on current option
        const remainingOptions = Object.keys(selectedOptions).filter((option) => option !== currentOption);
        for (const option of remainingOptions) {
          const variants = newFilteredVariants[option].filter((variant) =>
            variant.selectedOptions.some((variantOption) => variantOption.value === selectedOptions[currentOption]),
          );
          newFilteredVariants[option] = variants;
        }
      }

      setFilteredVariants(newFilteredVariants);
    };

    const setVariant = (newOptions: { [key: string]: string }) => {
      const selectedValues = Object.values(newOptions);
      //if all options are selected
      if (selectedValues.every((value) => !!value)) {
        // TODO: improve a little performance by using filteredVariants instead of product.variants
        const variantFound =
          product.variants.find((variant) => varitantHasSelectedOptions(variant, newOptions)) ?? null;
        setSelectedVariant(variantFound);
      } else {
        setSelectedVariant(null);
      }
    };
    filterVariants(selectedOptions, optionsQueue);
    setVariant(selectedOptions);
  }, [selectedOptions, optionsQueue, product.options, product.variants]);

  const handleArrowClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const scrollToProductThumbnails = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const targetElement = document.querySelector("#productThumbnails");
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const price = selectedVariant
    ? (selectedVariant.price.amount as number)
    : (product.priceRange.minVariantPrice.amount as number);

  const originalPrice = Math.ceil(price * 1.25);

  const showDiscount = isBlackFridayPromoEnabled();

  const discountedPrice = Math.round(price * 0.85 * 100) / 100;

  const availableForSale = product.availableForSale && (product.totalInventory ?? 0) >= MIN_QUANTITY_AVAILABLE;

  return (
    <div>
      <h1 className="hidden pt-2 dark:text-white sm:block sm:font-medium md:text-lg">{product.title}</h1>
      <div className="grid gap-2 md:grid-cols-3 md:gap-4">
        <div className="md:col-span-4">
          {showDiscount && (
            <p className="font-ligh py-0 text-red-600">
              <span className="mr-2 rounded-md bg-red-600 px-1 text-white">-40%</span>Limited time offer!
            </p>
          )}
          <p className="pt-0 text-xs font-light dark:text-white md:text-sm">
            Original price: <span className="original-price text-sm line-through md:text-base">$ {originalPrice}</span>
          </p>
          <p className="pt-0 text-2xl font-light dark:text-white md:pt-4 md:text-3xl md:font-semibold">
            <span id="value" className={showDiscount ? "line-through" : ""}>
              ${price}
            </span>
            {showDiscount && (
              <span id="discount-value" className="text-red-600">
                {` $${discountedPrice}`}
              </span>
            )}
            <span id="curency" className="pl-1 text-base sm:text-lg md:text-xl">
              {product.priceRange.minVariantPrice.currencyCode}
            </span>
          </p>
        </div>
      </div>
      <h2 className="sr-only">Product information</h2>

      <Form className="mt-3 md:mt-4" method="post" action={`/products/${product.handle}`}>
        <input name="variant" type="text" value={selectedVariant?.id ?? ""} readOnly hidden />
        {showOptions &&
          product.options?.map((option) => (
            <div className="pb-4" key={option.id}>
              <h3 className="text-lg font-semibold dark:text-white">{option.name}</h3>

              <RadioGroup
                value={selectedOptions[option.name]}
                onChange={handleChangePerOption(option.name)}
                {...(isDesktop ? { onClick: scrollToProductThumbnails } : {})}
                className="mt-4"
              >
                <RadioGroup.Label className="sr-only">Choose a {option.name}</RadioGroup.Label>
                <div className="grid grid-cols-3 gap-2.5 md:gap-6">
                  {option.values?.map((value) => {
                    const isEnabled = isOptionEnabled(option.name, value);
                    return (
                      <RadioGroup.Option
                        key={value}
                        value={value}
                        disabled={!isEnabled || transition.state === "submitting"}
                        onClick={
                          // reset option if same value is selected again
                          selectedOptions[option.name] === value
                            ? () => {
                                const newOptions = { ...selectedOptions, [option.name]: "" };
                                const newQueue = optionsQueue.filter((opt) => opt !== option.name);
                                changePurchaseGridState(newOptions, newQueue);
                              }
                            : undefined
                        }
                        className={({ active }) =>
                          classNames(
                            isEnabled && transition.state !== "submitting"
                              ? "cursor-pointer hover:text-admirerBlue"
                              : "cursor-not-allowed",
                            // active ? 'ring-2 ring-indigo-500' : '',
                            "group relative text-sm font-medium focus:outline-none sm:flex-1",
                          )
                        }
                      >
                        {({ checked }) =>
                          isEnabled ? ( // si tiene stock en ves de value
                            <Neum className="h-full" inset={checked}>
                              <RadioGroup.Label
                                className={classNames(
                                  checked ? "text-admirerBlue" : "",
                                  "flex h-full items-center justify-center p-1 py-3 text-center",
                                )}
                                as="p"
                              >
                                {value}
                              </RadioGroup.Label>
                            </Neum>
                          ) : (
                            <div className="flex h-full items-center justify-center p-1">
                              <RadioGroup.Label
                                className="justify-center py-2 text-center text-admirerBlack-200"
                                as="p"
                              >
                                {value}
                              </RadioGroup.Label>
                              <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -inset-px m-0 rounded-md border-2 border-admirerBlack-900"
                              >
                                <svg
                                  className="absolute inset-0 h-full w-full  stroke-1 text-admirerBlack-900"
                                  viewBox="0 0 100 100"
                                  preserveAspectRatio="none"
                                  stroke="currentColor"
                                  role="img"
                                  aria-label="Divider"
                                >
                                  <line x1={0} y1={100} x2={100} y2={0} vectorEffect="non-scaling-stroke" />
                                </svg>
                              </div>
                            </div>
                          )
                        }
                      </RadioGroup.Option>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>
          ))}

        <Badges product={product} />
        <div className="hidden  grid-cols-6 gap-4 lg:grid">
          <div className="col-span-6 md:col-span-4">
            <NeumButton rounded={true} disabled={!availableForSale || !selectedVariant}>
              <button
                id="add-to-cart"
                disabled={!availableForSale || !selectedVariant}
                type="submit"
                className={`text-white-700 group-hover:text-white-800 block w-full border-transparent bg-transparent text-center text-sm font-bold focus:border-transparent focus:ring-0 ${
                  selectedVariant ? "" : "cursor-not-allowed"
                }`}
              >
                {!availableForSale ? (
                  "Out of stock"
                ) : selectedVariant ? (
                  transition.state === "submitting" ? (
                    <img className="mx-auto my-[-18px] h-14 w-14" src={loader} alt="Adding to cart" />
                  ) : (
                    "Add to cart"
                  )
                ) : (
                  "Select Option"
                )}
              </button>
            </NeumButton>
          </div>
        </div>

        <div className=" fixed bottom-0 z-40 -mb-px -ml-4 w-full justify-center bg-admirerWhite px-5 py-6 shadow-add-cart dark:bg-admirerBlack md:-ml-6 lg:hidden">
          <div className="grid grid-cols-6 gap-2.5">
            <div className="flex items-center">
              <Neum rounded={true}>
                <ArrowUpIcon
                  className="m-2 h-6 w-6 flex-shrink-0 rounded-full p-0 group-hover:text-admirerBlue"
                  aria-hidden="true"
                  onClick={handleArrowClick}
                />
              </Neum>
            </div>

            <div className="col-span-5">
              {selectedVariant ? (
                <NeumButton rounded={true} disabled={!availableForSale}>
                  <button
                    id="add-to-cart"
                    type="submit"
                    disabled={!availableForSale}
                    className="text-white-700 group-hover:text-white-800 block max-h-11 w-full border-transparent bg-transparent text-center text-base font-bold focus:border-transparent focus:ring-0"
                  >
                    {!availableForSale ? (
                      "Out of stock"
                    ) : transition.state === "submitting" ? (
                      <img className="mx-auto my-[-16px] h-14 w-14" src={loader} alt="Adding to cart" />
                    ) : (
                      "Add to cart"
                    )}
                  </button>
                </NeumButton>
              ) : (
                <NeumButton rounded={true} disabled={!availableForSale}>
                  <button
                    type="button"
                    className="text-white-700 group-hover:text-white-800 block w-full border-transparent bg-transparent text-center text-base font-bold focus:border-transparent focus:ring-0"
                    onClick={scrollToProductThumbnails}
                  >
                    Select Option
                  </button>
                </NeumButton>
              )}
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default PurchaseGrid;
